/*
 * BFI Groupe — Landing Page (i18n)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowRight, Shield, Smartphone, Globe2, BadgeEuro, CreditCard, TrendingUp,
  Headphones, Check, Star, Sparkles, Gift, Plane, Bot, UserPlus,
  ShieldCheck, Wallet, MessageSquare, Repeat, Send, FileText, Lock, Zap,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { useLanguage } from '../lib/LanguageContext';

function useCountUp(target, duration = 2000, start = false) {
  const [value, setValue] = useState(0);
  const startTime = useRef(null);
  useEffect(() => {
    if (!start) return;
    let raf;
    const animate = (t) => {
      if (!startTime.current) startTime.current = t;
      const elapsed = t - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(target * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
}

function StatsCounter() {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const clients = useCountUp(120000, 2000, visible);
  const transactions = useCountUp(5000000, 2000, visible);
  const countries = useCountUp(50, 2000, visible);
  const rating = useCountUp(49, 2000, visible);
  const localeMap = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', de: 'de-DE', it: 'it-IT', nl: 'nl-NL', pt: 'pt-PT' };
  const fmt = (n) => n.toLocaleString(localeMap[language] || 'fr-FR');
  return (
    <section ref={ref} className="py-16 bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-900 text-white">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <div>
          <div className="text-4xl md:text-5xl font-bold">{fmt(clients)}+</div>
          <div className="text-blue-200 mt-2 text-sm uppercase tracking-widest">{t('landing.statsClients')}</div>
        </div>
        <div>
          <div className="text-4xl md:text-5xl font-bold">{fmt(transactions)}+</div>
          <div className="text-blue-200 mt-2 text-sm uppercase tracking-widest">{t('landing.statsTransactions')}</div>
        </div>
        <div>
          <div className="text-4xl md:text-5xl font-bold">{fmt(countries)}+</div>
          <div className="text-blue-200 mt-2 text-sm uppercase tracking-widest">{t('landing.statsCountries')}</div>
        </div>
        <div>
          <div className="text-4xl md:text-5xl font-bold">{(rating/10).toFixed(1)}/5</div>
          <div className="text-blue-200 mt-2 text-sm uppercase tracking-widest">{t('landing.statsRating')}</div>
        </div>
      </div>
    </section>
  );
}

function NewsletterSignup() {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error(t('landing.invalidEmail')); return; }
    setLoading(true);
    setTimeout(() => {
      toast.success(t('landing.subscribeSuccess'));
      setEmail(''); setLoading(false);
    }, 800);
  };
  return (
    <section className="py-20 bg-gradient-to-br from-blue-700 via-indigo-700 to-blue-800 text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl"></div>
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-semibold mb-4">
          <Zap className="w-3 h-3" /> {t('landing.newsletterBadge')}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold">{t('landing.newsletterTitle')}</h2>
        <p className="mt-3 text-blue-100">{t('landing.newsletterSubtitle')}</p>
        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('landing.emailPlaceholder')}
            className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200 focus:bg-white/20"
            data-testid="input-newsletter-email"
          />
          <Button type="submit" disabled={loading} className="bg-white text-blue-900 hover:bg-slate-100 disabled:opacity-60" data-testid="btn-newsletter-subscribe">
            {loading ? t('landing.sending') : t('landing.subscribe')}
          </Button>
        </form>
        <p className="mt-3 text-xs text-blue-200">{t('landing.noSpam')}</p>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t } = useLanguage();

  const features = [
    { icon: BadgeEuro, title: t('features.accountsSavings'), desc: t('features.accountsSavingsDesc'), to: '/comptes' },
    { icon: CreditCard, title: t('features.premiumCards'), desc: t('features.premiumCardsDesc'), to: '/cartes' },
    { icon: Send, title: t('features.transfers'), desc: t('features.transfersDesc'), to: '/dashboard/virement' },
    { icon: TrendingUp, title: t('features.stockRealtime'), desc: t('features.stockRealtimeDesc'), to: '/bourse' },
    { icon: Repeat, title: t('features.currencyConversion'), desc: t('features.currencyConversionDesc'), to: '/devises' },
    { icon: MessageSquare, title: t('features.chatbotAI'), desc: t('features.chatbotAIDesc'), to: '/aide' },
  ];

  const premiumFeatures = [
    { icon: Gift, title: t('premium.cashback'), desc: t('premium.cashbackDesc'), color: 'from-pink-500 to-rose-500' },
    { icon: Plane, title: t('premium.travelInsurance'), desc: t('premium.travelInsuranceDesc'), color: 'from-sky-500 to-blue-500' },
    { icon: Bot, title: t('premium.roboAdvisor'), desc: t('premium.roboAdvisorDesc'), color: 'from-purple-500 to-indigo-500' },
    { icon: UserPlus, title: t('premium.referral'), desc: t('premium.referralDesc'), color: 'from-emerald-500 to-green-600' },
    { icon: ShieldCheck, title: t('premium.virtualCard'), desc: t('premium.virtualCardDesc'), color: 'from-amber-500 to-orange-500' },
    { icon: Wallet, title: t('premium.multiBank'), desc: t('premium.multiBankDesc'), color: 'from-cyan-500 to-teal-500' },
  ];

  const pricing = [
    { name: 'Basic', price: '0', features: [t('pricing.freeCard'), t('pricing.freeTransfers'), t('pricing.mobileApp'), t('pricing.emailSupport')], style: 'basic', cta: t('common.signup') },
    { name: 'Premium', price: '9,99', features: [t('pricing.premiumCard'), t('pricing.cashback'), t('pricing.travelInsurance'), t('pricing.phoneSupport')], style: 'premium', cta: t('pricing.chooseOffer'), highlight: true },
    { name: 'Elite', price: '19,99', features: [t('pricing.metalCard'), t('pricing.cashback2'), t('pricing.premiumTravelInsurance'), t('pricing.personalConcierge'), t('pricing.loungeAccess')], style: 'elite', cta: t('pricing.chooseOffer') },
  ];

  const testimonials = [
    { name: 'Marie L.', role: t('testimonials.marieRole'), text: t('testimonials.marie'), initial: 'M' },
    { name: 'Thomas R.', role: t('testimonials.thomasRole'), text: t('testimonials.thomas'), initial: 'T' },
    { name: 'Sophie B.', role: t('testimonials.sophieRole'), text: t('testimonials.sophie'), initial: 'S' },
  ];

  const trustBadges = [
    { label: 'FGDR', desc: t('landing.trustFgdr') },
    { label: 'ACPR', desc: t('landing.trustAcpr') },
    { label: 'TLS 1.3', desc: t('landing.trustTls') },
    { label: '3D Secure', desc: t('landing.trust3ds') },
    { label: 'ISO 27001', desc: t('landing.trustIso') },
    { label: 'RGPD', desc: t('landing.trustRgpd') },
  ];

  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
    { q: t('faq.q6'), a: t('faq.a6') },
  ];

  const advantages = [
    { icon: Shield, title: t('landing.maxSecurity'), desc: t('landing.maxSecurityDesc') },
    { icon: BadgeEuro, title: t('landing.noHiddenFees'), desc: t('landing.noHiddenFeesDesc') },
    { icon: Headphones, title: t('landing.support247'), desc: t('landing.support247Desc') },
    { icon: Globe2, title: t('landing.international'), desc: t('landing.internationalDesc') },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-blue-900"></div>
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-32 -left-20 w-[600px] h-[600px] bg-indigo-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white animate-fadeIn">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-6">
              <Sparkles className="w-3 h-3" /> {t('hero.badge')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight tracking-tight">
              {t('hero.title1')} <br/><span className="bg-gradient-to-r from-blue-300 via-indigo-300 to-purple-300 bg-clip-text text-transparent">{t('hero.title2')}</span>.
            </h1>
            <p className="mt-6 text-lg text-blue-100 max-w-xl">
              {t('hero.subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/inscription">
                <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100" data-testid="btn-hero-signup">
                  {t('common.signup')} <ArrowRight className="ml-2 w-4 h-4"/>
                </Button>
              </Link>
              <Link to="/connexion">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="btn-hero-login">
                  {t('common.login')}
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-blue-100">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> {t('hero.acpr')}</span>
              <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-400" /> {t('hero.tls')}</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> {t('hero.fgdr')}</span>
            </div>
          </div>
          <div className="relative animate-fadeIn">
            <div className="bfi-card elite mx-auto" style={{maxWidth:480}}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-70">BFI Groupe</div>
                  <div className="text-xl font-semibold">{t('landing.eliteCardName')}</div>
                </div>
                <div className="text-2xl font-bold italic">mastercard</div>
              </div>
              <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 text-[10px] uppercase tracking-widest"><Sparkles className="w-3 h-3"/> {t('landing.metalBadge')}</div>
              <div className="mt-10 text-2xl tracking-[6px] font-mono">5497  ••••  ••••  8842</div>
              <div className="flex items-end justify-between mt-6">
                <div>
                  <div className="text-[10px] opacity-60 uppercase">{t('card.cardHolder')}</div>
                  <div className="font-semibold tracking-wide">{t('landing.cardHolderName')}</div>
                </div>
                <div>
                  <div className="text-[10px] opacity-60 uppercase">Exp.</div>
                  <div className="font-mono">12/30</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <StatsCounter />

      {/* Features grid */}
      <section id="services" className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-blue-700 dark:text-blue-400 text-sm font-semibold uppercase tracking-widest">{t('landing.services')}</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 dark:text-white">{t('landing.servicesTitle')}</h2>
          <p className="text-slate-600 dark:text-slate-300 mt-3">{t('landing.servicesSubtitle')}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {features.map((s, i) => (
            <Link to={s.to} key={i} className="group p-6 rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-blue-500">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center mb-4 group-hover:scale-110 transition dark:bg-blue-900/50 dark:text-blue-300">
                <s.icon className="w-6 h-6"/>
              </div>
              <h3 className="font-semibold text-lg dark:text-white">{s.title}</h3>
              <p className="text-slate-600 text-sm mt-2 dark:text-slate-300">{s.desc}</p>
              <div className="mt-3 text-blue-700 text-sm font-medium inline-flex items-center gap-1 dark:text-blue-400">{t('landing.discover')} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition"/></div>
            </Link>
          ))}
        </div>
      </section>

      {/* Premium features */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 text-sm font-semibold uppercase tracking-widest"><Sparkles className="w-4 h-4" /> {t('landing.premiumBadge')}</div>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 dark:text-white">{t('landing.premiumTitle')}</h2>
            <p className="text-slate-600 dark:text-slate-300 mt-3">{t('landing.premiumSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {premiumFeatures.map((f, i) => (
              <div key={i} className="relative p-6 rounded-2xl bg-white border border-slate-200 hover:shadow-2xl hover:-translate-y-1 transition-all dark:bg-slate-800 dark:border-slate-700">
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r ${f.color}`}></div>
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${f.color} text-white flex items-center justify-center mb-4 shadow-lg`}>
                  <f.icon className="w-7 h-7"/>
                </div>
                <h3 className="font-semibold text-lg dark:text-white">{f.title}</h3>
                <p className="text-slate-600 text-sm mt-2 dark:text-slate-300">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="py-16 bg-white dark:bg-slate-950 border-y border-slate-100 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-blue-700 dark:text-blue-400 text-sm font-semibold uppercase tracking-widest">{t('landing.trustSection')}</div>
            <h2 className="text-2xl md:text-3xl font-bold mt-2 dark:text-white">{t('landing.trustTitle')}</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {trustBadges.map((b, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-center hover:border-blue-300 transition">
                <div className="text-base font-bold tracking-tight text-blue-900 dark:text-blue-300">{b.label}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8">
          {advantages.map((a, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-blue-800 dark:bg-slate-800 dark:border-slate-700 dark:text-blue-300">
                <a.icon className="w-6 h-6"/>
              </div>
              <div>
                <div className="font-semibold dark:text-white">{a.title}</div>
                <div className="text-slate-600 dark:text-slate-300 text-sm">{a.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-blue-700 dark:text-blue-400 text-sm font-semibold uppercase tracking-widest">{t('landing.pricing')}</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 dark:text-white">{t('landing.pricingTitle')}</h2>
          <p className="text-slate-600 dark:text-slate-300 mt-3">{t('landing.pricingSubtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {pricing.map((p) => (
            <div key={p.name} className={`relative rounded-2xl p-7 border ${p.highlight ? 'border-blue-700 ring-2 ring-blue-200 dark:ring-blue-900 shadow-2xl' : 'border-slate-200 dark:border-slate-700'} bg-white dark:bg-slate-800`}>
              {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-700 text-white text-xs font-semibold px-3 py-1 rounded-full">{t('landing.popular')}</div>}
              <div className={`bfi-card ${p.style} mb-6`} style={{maxWidth:'100%'}}>
                <div className="flex items-center justify-between">
                  <div className="text-sm uppercase tracking-widest">{p.name}</div>
                  <div className="text-xl font-bold italic">{p.style === 'elite' ? 'mastercard' : 'VISA'}</div>
                </div>
                {p.style === 'elite' && <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 text-[10px] uppercase tracking-widest"><Sparkles className="w-3 h-3"/> {t('landing.metalBadgeShort')}</div>}
                <div className="mt-10 font-mono tracking-widest text-sm">•••• •••• •••• 4321</div>
                <div className="flex justify-between mt-4 text-xs opacity-90">
                  <span>BFI GROUPE</span><span>12/30</span>
                </div>
              </div>
              <div className="text-3xl font-bold dark:text-white">{p.price}€<span className="text-base font-medium text-slate-500 dark:text-slate-400">{t('landing.perMonth')}</span></div>
              <ul className="mt-5 space-y-2 text-sm">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 dark:text-slate-300"><Check className="w-4 h-4 text-emerald-600 mt-0.5"/> {f}</li>
                ))}
              </ul>
              <Link to="/inscription"><Button className={`mt-6 w-full ${p.highlight ? 'bg-blue-700 hover:bg-blue-800' : 'bg-slate-900 hover:bg-slate-800'}`} data-testid={`btn-pricing-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>{p.cta}</Button></Link>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-blue-700 dark:text-blue-400 text-sm font-semibold uppercase tracking-widest">{t('landing.testimonials')}</div>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 dark:text-white">{t('landing.testimonialsTitle')}</h2>
            <p className="text-slate-600 dark:text-slate-300 mt-3">{t('landing.testimonialsSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {testimonials.map((tm, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200 dark:bg-slate-800 dark:border-slate-700 hover:shadow-xl transition">
                <div className="flex gap-0.5 text-amber-400 mb-3">{[1,2,3,4,5].map(s=><Star key={s} className="w-4 h-4 fill-current"/>)}</div>
                <p className="text-slate-700 dark:text-slate-200">«&nbsp;{tm.text}&nbsp;»</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 flex items-center justify-center font-semibold">{tm.initial}</div>
                  <div><div className="font-semibold text-sm dark:text-white">{tm.name}</div><div className="text-slate-500 dark:text-slate-400 text-xs">{tm.role}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 max-w-3xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-blue-700 dark:text-blue-400 text-sm font-semibold uppercase tracking-widest">{t('landing.faq')}</div>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 dark:text-white">{t('landing.faqTitle')}</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`q-${i}`} className="dark:border-slate-700">
              <AccordionTrigger className="text-left dark:text-white">{f.q}</AccordionTrigger>
              <AccordionContent className="dark:text-slate-300">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Newsletter */}
      <NewsletterSignup />
    </PublicLayout>
  );
}
